import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileSpreadsheet, Eye, Filter, CheckCircle, Clock, Calendar, Search, RefreshCw, ChevronDown, ChevronRight, Package, ClipboardList, PackageCheck, XCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import * as authApi from '@/services/authApi';

const FGPackedHistory = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const isAdmin = user && user.role === 'Admin';
  
  const [packedSlips, setPackedSlips] = useState([]);
  const [summary, setSummary] = useState({ total_slips: 0, total_pick_slip_qty: 0, total_packed_qty: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 100;
  
  // Detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedSlip, setSelectedSlip] = useState(null);
  
  // Track expanded items to show picked USNs
  const [expandedItems, setExpandedItems] = useState({});
  
  // Force Cancel dialog (Admin only)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelSlipNumber, setCancelSlipNumber] = useState('');
  const [cancelSlipDC, setCancelSlipDC] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [forceCancel, setForceCancel] = useState(false);
  
  // Success dialog
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [cancelledSlipInfo, setCancelledSlipInfo] = useState(null);

  // Fetch user data on mount
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await authApi.getMe();
        setUser(userData);
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    fetchPackedSlips();
  }, [startDate, endDate, currentPage]);

  const fetchPackedSlips = async () => {
    try {
      setLoading(true);
      const params = [];
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      if (searchTerm) params.push(`search=${encodeURIComponent(searchTerm)}`);
      params.push(`page=${currentPage}`);
      params.push(`page_size=${pageSize}`);
      
      const queryString = params.length > 0 ? '?' + params.join('&') : '';
      const response = await apiClient.get(`/wms/fg/pick-slips/packed-history${queryString}`);
      setPackedSlips(response.data.pick_slips || []);
      setSummary(response.data.summary || { total_slips: 0, total_pick_slip_qty: 0, total_packed_qty: 0 });
      setTotalPages(response.data.total_pages || 1);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch packed slips');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchPackedSlips();
  };

  const handleViewDetail = async (slipNumber) => {
    try {
      const response = await apiClient.get(`/wms/fg/pick-slip-detail?slip_number=${encodeURIComponent(slipNumber)}`);
      setSelectedSlip(response.data);
      setExpandedItems({}); // Reset expanded state when opening new slip
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch slip details');
    }
  };

  // Toggle expanded state for an item to show/hide picked USNs
  const toggleItemExpanded = (itemIdx) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemIdx]: !prev[itemIdx]
    }));
  };

  // Open force cancel dialog (Admin only)
  const openCancelDialog = (slipNumber, dcNumber) => {
    setCancelSlipNumber(slipNumber);
    setCancelSlipDC(dcNumber || '');
    setCancellationReason('');
    setForceCancel(false);
    setCancelDialogOpen(true);
  };

  // Handle force cancel packed slip
  const handleForceCancel = async () => {
    if (!cancellationReason.trim()) {
      toast.error('Cancellation reason is mandatory');
      return;
    }
    if (!forceCancel) {
      toast.error('You must check the Force Cancel checkbox to proceed');
      return;
    }

    try {
      setCancelSubmitting(true);
      const response = await apiClient.post('/wms/fg/pick-slips/cancel', {
        slip_number: cancelSlipNumber,
        cancellation_reason: cancellationReason.trim(),
        force_cancel: true
      });
      
      setCancelDialogOpen(false);
      
      // Store cancelled slip info for success dialog
      setCancelledSlipInfo({
        slipNumber: cancelSlipNumber,
        pendingPutAwayCount: response.data.pending_put_away_count || 0,
        cancelledBy: response.data.cancelled_by,
        unlinkedDC: response.data.unlinked_dc,
        previousStatus: response.data.previous_status
      });
      
      setSuccessDialogOpen(true);
      fetchPackedSlips();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel packed slip');
    } finally {
      setCancelSubmitting(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const filteredSlips = packedSlips.filter(slip =>
    slip.pick_slip_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms/fg/pick-slips')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Packed Slips History</h1>
              <p className="text-sm text-[#E8DCC8]">View all fully packed pick slips</p>
            </div>
          </div>
          <Button
            onClick={fetchPackedSlips}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Summary Tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <Card className="bg-white border-[#E5DFD0]" data-testid="total-slips-tile">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FileSpreadsheet className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-[#5A5F6E]">Total Slips</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]">{summary.total_slips}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-[#E5DFD0]" data-testid="pick-slip-qty-tile">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <ClipboardList className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-[#5A5F6E]">Pick Slip Qty</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]">{summary.total_pick_slip_qty?.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-[#E5DFD0]" data-testid="packed-qty-tile">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-lg">
                  <PackageCheck className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-[#5A5F6E]">Packed Qty</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]">{summary.total_packed_qty?.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Date Filter */}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#5A5F6E]" />
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-36"
                placeholder="From"
                data-testid="start-date-filter"
              />
              <span className="text-[#5A5F6E]">to</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-36"
                placeholder="To"
                data-testid="end-date-filter"
              />
              {(startDate || endDate) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setStartDate(''); setEndDate(''); }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  Clear
                </Button>
              )}
            </div>
            <div className="flex-1 min-w-[200px] flex gap-2">
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search by slip number"
                className="max-w-md"
                data-testid="search-input"
              />
              <Button
                onClick={handleSearch}
                variant="outline"
                className="border-[#E5DFD0]"
              >
                <Search className="w-4 h-4" />
              </Button>
            </div>
            <div className="text-sm text-[#5A5F6E]">
              {totalCount.toLocaleString()} packed slip(s)
            </div>
          </div>
        </div>

        {/* Packed Slips Table */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden">
          <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            <h2 className="font-semibold text-emerald-800">Packed Pick Slips</h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-[#5A5F6E]">Loading...</div>
          ) : filteredSlips.length === 0 ? (
            <div className="p-12 text-center">
              <FileSpreadsheet className="w-12 h-12 text-[#E5DFD0] mx-auto mb-4" />
              <p className="text-[#5A5F6E]">No packed slips found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-[#E5DFD0]">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Slip Number</th>
                    <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Customer</th>
                    <th className="px-4 py-3 text-center font-medium text-[#1A1F2E]">Items</th>
                    <th className="px-4 py-3 text-right font-medium text-[#1A1F2E]">Total Qty</th>
                    <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">DC Number</th>
                    <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Picker</th>
                    <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Packed Date</th>
                    <th className="px-4 py-3 text-center font-medium text-[#1A1F2E]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSlips.map((slip) => (
                    <tr key={slip.slip_id} className="border-b border-[#E5DFD0] hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono font-medium text-[#1A1F2E]">
                        {slip.pick_slip_number}
                      </td>
                      <td className="px-4 py-3 text-[#5A5F6E]">
                        {slip.customer || '-'}
                      </td>
                      <td className="px-4 py-3 text-center">{slip.total_items}</td>
                      <td className="px-4 py-3 text-right font-medium">{slip.total_qty?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-[#5A5F6E]">
                        {slip.dc_number || '-'}
                      </td>
                      <td className="px-4 py-3 text-[#5A5F6E]" data-testid={`picker-name-${slip.slip_id}`}>
                        {slip.picker_name ? (
                          <div>
                            <span className="font-medium text-[#1A1F2E]">{slip.picker_name}</span>
                            {slip.picker_staff_id && (
                              <span className="text-xs text-gray-400 ml-1">({slip.picker_staff_id.replace('ID No : ', '')})</span>
                            )}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 text-[#5A5F6E] text-sm">
                        {formatDate(slip.packed_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewDetail(slip.pick_slip_number)}
                            className="text-blue-600"
                            title="View Details"
                            data-testid={`view-detail-${slip.slip_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {/* Force Cancel button - Admin only */}
                          {isAdmin && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openCancelDialog(slip.pick_slip_number, slip.dc_number)}
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                              title="Force Cancel (Admin Only)"
                              data-testid={`force-cancel-${slip.slip_id}`}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 px-2">
              <p className="text-sm text-[#5A5F6E]">
                Showing {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, totalCount)} of {totalCount.toLocaleString()} slips
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  data-testid="packed-history-prev"
                >
                  Previous
                </Button>
                <span className="text-sm text-[#5A5F6E] px-2">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  data-testid="packed-history-next"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pick Slip Details - {selectedSlip?.pick_slip_number}</DialogTitle>
          </DialogHeader>
          {selectedSlip && (
            <div className="py-4 space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-xs text-[#5A5F6E]">Status</p>
                  <Badge className="bg-emerald-100 text-emerald-700 font-medium">Packed</Badge>
                </div>
                <div>
                  <p className="text-xs text-[#5A5F6E]">Total Items</p>
                  <p className="font-medium">{selectedSlip.total_items}</p>
                </div>
                <div>
                  <p className="text-xs text-[#5A5F6E]">Total Qty</p>
                  <p className="font-medium">{selectedSlip.total_qty?.toLocaleString()}</p>
                </div>
                {selectedSlip.dc_number && (
                  <div>
                    <p className="text-xs text-[#5A5F6E]">DC Number</p>
                    <p className="font-medium">{selectedSlip.dc_number}</p>
                  </div>
                )}
                {selectedSlip.transport_name && (
                  <div>
                    <p className="text-xs text-[#5A5F6E]">Transporter</p>
                    <p className="font-medium">{selectedSlip.transport_name}</p>
                  </div>
                )}
                {selectedSlip.packed_at && (
                  <div>
                    <p className="text-xs text-[#5A5F6E]">Packed Date</p>
                    <p className="font-medium">{formatDate(selectedSlip.packed_at)}</p>
                  </div>
                )}
                {selectedSlip.picker_name && (
                  <div>
                    <p className="text-xs text-[#5A5F6E]">Picker</p>
                    <p className="font-medium">{selectedSlip.picker_name}
                      {selectedSlip.picker_staff_id && (
                        <span className="text-xs text-gray-400 ml-1">({selectedSlip.picker_staff_id.replace('ID No : ', '')})</span>
                      )}
                    </p>
                  </div>
                )}
              </div>

              {/* Items List */}
              <div>
                <h3 className="font-medium text-[#1A1F2E] mb-2">Items ({selectedSlip.items?.length})</h3>
                <div className="max-h-[400px] overflow-y-auto border border-[#E5DFD0] rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-2 text-left w-8"></th>
                        <th className="px-3 py-2 text-left">Barcode</th>
                        <th className="px-3 py-2 text-left">Design</th>
                        <th className="px-3 py-2 text-right">Required</th>
                        <th className="px-3 py-2 text-right">Picked</th>
                        <th className="px-3 py-2 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedSlip.items?.map((item, idx) => (
                        <React.Fragment key={idx}>
                          {/* Item Row */}
                          <tr 
                            className={`border-t border-[#E5DFD0] cursor-pointer hover:bg-gray-50 ${expandedItems[idx] ? 'bg-amber-50' : ''}`}
                            onClick={() => item.picked_packs?.length > 0 && toggleItemExpanded(idx)}
                          >
                            <td className="px-3 py-2">
                              {item.picked_packs?.length > 0 && (
                                expandedItems[idx] 
                                  ? <ChevronDown className="w-4 h-4 text-amber-600" />
                                  : <ChevronRight className="w-4 h-4 text-gray-400" />
                              )}
                            </td>
                            <td className="px-3 py-2 font-mono">{item.barcode}</td>
                            <td className="px-3 py-2">{item.design_name || '-'}</td>
                            <td className="px-3 py-2 text-right">{item.required_qty}</td>
                            <td className="px-3 py-2 text-right font-medium">
                              <span className="inline-flex items-center gap-1">
                                {item.picked_qty || 0}
                                {item.picked_packs?.length > 0 && (
                                  <Package className="w-3 h-3 text-amber-500" />
                                )}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <CheckCircle className="w-4 h-4 text-green-600 mx-auto" />
                            </td>
                          </tr>
                          
                          {/* Expanded Picked USNs Row */}
                          {expandedItems[idx] && item.picked_packs?.length > 0 && (
                            <tr className="bg-amber-50/50">
                              <td colSpan="6" className="px-3 py-2">
                                <div className="ml-6 p-3 bg-white rounded-lg border border-amber-200">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Package className="w-4 h-4 text-amber-600" />
                                    <span className="font-medium text-amber-800">
                                      Picked USNs ({item.picked_packs.length})
                                    </span>
                                  </div>
                                  <div className="max-h-40 overflow-y-auto">
                                    <table className="w-full text-xs">
                                      <thead className="bg-amber-100 sticky top-0">
                                        <tr>
                                          <th className="px-2 py-1 text-left">#</th>
                                          <th className="px-2 py-1 text-left">USN Code</th>
                                          <th className="px-2 py-1 text-left">Bin</th>
                                          <th className="px-2 py-1 text-left">Picked At</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {item.picked_packs.map((pack, packIdx) => (
                                          <tr key={packIdx} className="border-t border-amber-100 hover:bg-amber-50">
                                            <td className="px-2 py-1 text-gray-500">{packIdx + 1}</td>
                                            <td className="px-2 py-1 font-mono font-medium text-amber-700">
                                              {pack.pack_barcode || pack.usn_code || '-'}
                                            </td>
                                            <td className="px-2 py-1 text-gray-600">
                                              {pack.bin_code || '-'}
                                            </td>
                                            <td className="px-2 py-1 text-gray-500">
                                              {formatDateTime(pack.picked_at)}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                  <ChevronRight className="w-3 h-3" /> Click on an item row to view picked USNs
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force Cancel Dialog (Admin Only) */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-5 h-5" />
              Force Cancel Packed Slip
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-sm text-orange-800">
                You are about to force cancel packed slip: <strong className="font-mono">{cancelSlipNumber}</strong>
              </p>
              {cancelSlipDC && (
                <p className="text-xs text-orange-700 mt-1">
                  DC Number: <span className="font-semibold">{cancelSlipDC}</span> (will be unlinked)
                </p>
              )}
              <p className="text-xs text-orange-600 mt-2">
                All packed pieces will be moved to Cancelled Items Put-Away queue.
              </p>
            </div>
            
            {/* Force Cancel Checkbox */}
            <div className="bg-red-50 border border-red-300 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="forceCancelCheckbox"
                  checked={forceCancel}
                  onChange={(e) => setForceCancel(e.target.checked)}
                  className="mt-1 h-4 w-4 text-red-600 border-red-300 rounded focus:ring-red-500"
                />
                <div>
                  <Label htmlFor="forceCancelCheckbox" className="text-red-800 font-semibold cursor-pointer">
                    I confirm this Force Cancel action
                  </Label>
                  <p className="text-xs text-red-700 mt-1">
                    This will cancel the packed slip and move all pieces to the put-away queue.
                  </p>
                  <p className="text-xs text-red-600 mt-1 font-medium">
                    ⚠️ This action cannot be undone. Use with caution.
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <Label className="text-red-700">
                Cancellation Reason <span className="text-red-500">*</span>
              </Label>
              <Textarea
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Please provide a reason for cancellation (mandatory)"
                className="mt-2 min-h-[100px]"
                data-testid="cancellation-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setCancelDialogOpen(false)}
              disabled={cancelSubmitting}
            >
              Go Back
            </Button>
            <Button
              onClick={handleForceCancel}
              disabled={cancelSubmitting || !cancellationReason.trim() || !forceCancel}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="confirm-force-cancel-btn"
            >
              {cancelSubmitting ? 'Cancelling...' : 'Force Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success Confirmation Dialog */}
      <Dialog open={successDialogOpen} onOpenChange={setSuccessDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              Packed Slip Force Cancelled
            </DialogTitle>
          </DialogHeader>
          <div className="py-6">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 text-center">
              <p className="text-sm text-gray-600 mb-2">Pick Slip Number:</p>
              <p className="text-2xl font-bold font-mono text-[#1A1F2E] mb-4">
                {cancelledSlipInfo?.slipNumber}
              </p>
              <div className="bg-orange-100 rounded-lg p-2 mb-3">
                <p className="text-xs text-orange-800 font-medium">
                  ⚠️ Force Cancelled by Admin
                </p>
                {cancelledSlipInfo?.previousStatus && (
                  <p className="text-xs text-orange-700">
                    Previous Status: {cancelledSlipInfo.previousStatus}
                  </p>
                )}
                {cancelledSlipInfo?.unlinkedDC && (
                  <p className="text-xs text-orange-700">
                    Unlinked from DC: {cancelledSlipInfo.unlinkedDC}
                  </p>
                )}
              </div>
              <div className="border-t border-orange-200 pt-4 mt-4">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">{cancelledSlipInfo?.pendingPutAwayCount || 0}</span> piece(s) moved to Put-Away Queue
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Cancelled by: {cancelledSlipInfo?.cancelledBy}
                </p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
              <p className="text-xs text-amber-800 text-center">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Go to <strong>Cancelled Pick Slip Put-Away</strong> to assign bins for cancelled pieces
              </p>
            </div>
          </div>
          <DialogFooter className="justify-center">
            <Button
              onClick={() => {
                setSuccessDialogOpen(false);
                setCancelledSlipInfo(null);
              }}
              className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white px-8"
              data-testid="close-success-dialog"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FGPackedHistory;
